<template>
  <div class="row row-spacing">
    <div class="col-md-12">
      <div class="card-body bbbee-content">
        <div class="row enterprise-status-row">
          <div class="col-md-6">
            <div class="form-group">
              <label>Enterprise Status</label>
              <v-select
                v-model="bbbee.status"
                :options="enterpriseStatusOptions"
                label="label"
                valueProp="value"
                :disabled="hasCertificate"
                :class="{ 'has-error': validationErrors.status }"
              ></v-select>
              <small class="has-error mt-1" v-if="validationErrors.status">
                {{ validationErrors.status }}
              </small>
            </div>
          </div>
        </div>

        <div class="row" v-if="showCertificateFields">
          <div class="col-md-12">
            <SupplierRequiredDocumentation
              ref="requiredDocumentation"
              :supplierNumber="supplierNumber"
              @certificate-changed="hasCertificate = $event"
            >
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group">
                    <label>B-BBEE Level</label>
                    <v-select
                      v-model="bbbee.level"
                      :options="bbbeeLevelOptions"
                      label="label"
                      valueProp="value"
                      :disabled="hasCertificate"
                      :class="{ 'has-error': validationErrors.level }"
                    ></v-select>
                    <small class="has-error mt-1" v-if="validationErrors.level">
                      {{ validationErrors.level }}
                    </small>
                  </div>
                </div>
              </div>
            </SupplierRequiredDocumentation>
          </div>
        </div>

        <div class="save-button-wrapper mt-3">
          <button
            class="btn btn-info m-0"
            :disabled="isSaving || hasCertificate"
            @click="updateBbbee()"
          >
            Save
          </button>
          <button
            v-if="showCertificateFields && hasCertificate"
            class="btn btn-primary m-0 ml-2"
            type="button"
            :disabled="isSaving"
            @click="$refs.requiredDocumentation.deleteCertificate()"
          >
            Delete Certificate
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SupplierRequiredDocumentation from '@/components/Suppliers/SupplierRequiredDocumentation'
export default {
  components: {
    SupplierRequiredDocumentation
  },
  data: function() {
    return {
      supplierNumber: 0,
      supplier: {},
      bbbee: {},
      bbbeeCertificate: {
        fileName: '',
        description: '',
        expiryDate: '1970-01-01',
        type: ''
      },
      certificateWarning: false,
      hasCertificate: false,
      isSaving: false,
      validationErrors: {},
      bbbeeLevelOptions: [],
      enterpriseStatusOptions: [
        { value: "Generic", label: "Generic Enterprise" },
        { value: "EME", label: "Exempted Micro Enterprise" },
        { value: "QSE", label: "Qualifying Small Enterprise" },
        { value: "NON_COMPLIANT", label: "Non-Compliant" },
        { value: "NO_CERTIFICATE", label: "No Certificate" },
      ],
    };
  },
  created() {
    this.supplierNumber = parseInt(this.$route.params.supplierNumber);
    this.$root.$emit("SET_PAGE_TITLE", this.$route.meta.title);
  },
  mounted: function() {
    this.getBbbeeLevels()
      .then(() => {
        this.getSupplier();
      })
      .catch((error) => {
        console.error(error);
        this.showError("Error Fetching B-BBEE Levels", error);
      });
  },
  computed: {
    showCertificateFields() {
      return this.bbbee.status !== "NO_CERTIFICATE";
    },
  },
  methods: {
    getBbbeeLevels() {
      return this.$http.get(this.$config.aimsAPIv3 + "suppliers/bbbee/levels")
        .then((response) => {
          this.bbbeeLevelOptions = response.data;
        });
    },
    defaultBbbeeLevel() {
      return this.bbbeeLevelOptions.length ? this.bbbeeLevelOptions[0].value : null;
    },
    normalizeEnumValue(value, options, fallbackValue) {
      if (!value) {
        return fallbackValue;
      }

      if (typeof value === "string") {
        const matchedOption = options.find((option) => {
          return option.value === value || option.label === value;
        });

        return matchedOption ? matchedOption.value : fallbackValue;
      }

      if (typeof value === "object" && value.value) {
        return value.value;
      }

      return fallbackValue;
    },
    getSupplier() {
      this.$http
        .get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber)
        .then(
          (response) => {
            this.supplier = response.data;
            this.$root.$emit(
              "SET_PAGE_TITLE",
              this.$route.meta.title + " - " + this.supplier.name
            );
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Supplier", error);
          }
        )
        .finally(() => {
          this.getBbbee();
        });
    },
    getBbbee() {
      this.$http.get(this.$config.aimsAPIv3 + "suppliers/" + this.supplierNumber + "/bbbee")
        .then(
          (response) => {
            const data = response.data;
            
            this.bbbee = {
              ...data,
              level: this.normalizeEnumValue(data.level, this.bbbeeLevelOptions, this.defaultBbbeeLevel()),
              status: this.normalizeEnumValue(data.status, this.enterpriseStatusOptions, "Generic"),
            };
          },
          (error) => {
            console.error(error);
            this.showError("Error Fetching Supplier B-BBEE Details", error);
          }
        );
    },
    validateBbbee() {
      const errors = {};

      if (this.showCertificateFields && !this.bbbee.level) {
        errors.level = "Please select a B-BBEE level.";
      }

      if (!this.bbbee.status) {
        errors.status = "Please select an enterprise status.";
      }

      this.validationErrors = errors;
      return Object.keys(errors).length === 0;
    },
    async updateBbbee() {
      if (!this.validateBbbee()) {
        const firstValidationError = Object.values(this.validationErrors)[0];
        this.showWarningAlert(firstValidationError);
        return;
      }

      this.isSaving = true;

      try {
        if (this.showCertificateFields) {
          const certificateSaved = await this.$refs.requiredDocumentation.saveCertificate();
          if (!certificateSaved) {
            return;
          }
        }

        const payload = Object.assign({}, this.bbbee, {
          supplierNumber: this.supplierNumber,
          level: this.normalizeEnumValue(this.bbbee.level, this.bbbeeLevelOptions, this.defaultBbbeeLevel()),
          status: this.normalizeEnumValue(this.bbbee.status, this.enterpriseStatusOptions, "Generic"),
        });

        await this.$http.put(this.$config.aimsAPIv3 + "suppliers/bbbee/update", payload);
        this.showSuccess("Supplier B-BBEE details updated");
        this.getBbbee();
      } catch (error) {
        console.error(error);
        this.showError("Error Updating Supplier B-BBEE Details", error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style scoped>
.bbbee-content {
  min-height: 42rem;
}

.enterprise-status-row {
  min-height: 6rem;
}

.expiry-warning {
  color: #a94442;
  background-color: #f8d7da;
  border: 1px solid #f1b9bf;
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
}
</style>
